/**
 * DataTable (docs/04 §3, §5): table at pointer widths, record Rows below the
 * table breakpoint — never a shrunken table (docs/03 §6, brand standard §0).
 *
 * Density is real here, not cosmetic: every padding and the row height come
 * from `body[data-density]` tokens (vertical AND horizontal), which is what
 * makes compact clear the ">=30% more rows at 1280x800" bar docs/04 §5 sets.
 * Sorting is the SERVER's (docs/08 whitelists): headers emit onSort and render
 * aria-sort; the component never sorts client-side.
 *
 * Two affordances were missing and are now here (X-6.1, X-6.4's sibling):
 * a sortable header that is not the active sort shows a faint two-way chevron,
 * because without one it is visually identical to a header that does nothing;
 * and a column may be dragged wider by its trailing edge, with the width stored
 * per user per table.
 */

import { MAX_COLUMN_WIDTH, MIN_COLUMN_WIDTH } from '@propos/shared';
import { clsx } from 'clsx';
import { ArrowDown, ArrowUp, ChevronsUpDown } from 'lucide-react';
import { useRef, type PointerEvent as ReactPointerEvent, type ReactNode } from 'react';

export interface DataTableColumn<T> {
  /** Server sort key; column is sortable exactly when it has one. */
  readonly sortKey?: string | undefined;
  readonly header: string;
  /**
   * Catalog id, when this column came from a table catalog. Required for
   * resizing — a width has to be stored against something stable, and the
   * header text is neither stable nor unique across tables.
   */
  readonly id?: string | undefined;
  /** Stored width in pixels; absent lets the table size the column. */
  readonly width?: number | undefined;
  readonly align?: 'left' | 'right' | undefined;
  /** Cap the column so long values truncate instead of stretching the table. */
  readonly className?: string | undefined;
  readonly render: (row: T) => ReactNode;
}

export interface DataTableProps<T> {
  readonly columns: readonly DataTableColumn<T>[];
  readonly rows: readonly T[];
  readonly rowKey: (row: T) => string;
  /** Record-row rendering for <640px (docs/03 §6). */
  readonly mobileRow: (row: T) => ReactNode;
  readonly sort?: string | undefined;
  readonly dir?: 'asc' | 'desc' | undefined;
  readonly onSort?: ((sortKey: string) => void) | undefined;
  /** Accessible name for the table. */
  readonly label: string;
  /** Rendered in place of the body when rows is empty (docs/03 §3). */
  readonly empty?: ReactNode;
  readonly loading?: boolean | undefined;
  /**
   * Called when a column's trailing edge is released, with the new width.
   * Omit to make columns unresizable — which is the right default for tables
   * that are not backed by a saved layout.
   */
  readonly onResize?: ((columnId: string, width: number) => void) | undefined;
}

/**
 * The drag handle on a column's trailing edge.
 *
 * Pointer events rather than mouse events, so a pen or touch drag works; the
 * handle captures the pointer so the drag survives leaving the 6px strip. A
 * keyboard user is served by the two arrow-key buttons the same handle exposes,
 * because a drag with no keyboard equivalent is a control half the people using
 * this app cannot reach.
 */
function ResizeHandle({
  columnId,
  header,
  width,
  onResize,
}: {
  readonly columnId: string;
  readonly header: string;
  readonly width: number | undefined;
  readonly onResize: (columnId: string, width: number) => void;
}): ReactNode {
  const drag = useRef<{ startX: number; startWidth: number } | null>(null);

  const onPointerDown = (event: ReactPointerEvent<HTMLButtonElement>): void => {
    const cell = event.currentTarget.closest('th');
    drag.current = {
      startX: event.clientX,
      startWidth: width ?? cell?.getBoundingClientRect().width ?? MIN_COLUMN_WIDTH,
    };
    event.currentTarget.setPointerCapture(event.pointerId);
  };

  const onPointerMove = (event: ReactPointerEvent<HTMLButtonElement>): void => {
    const state = drag.current;
    if (state === null) return;
    onResize(columnId, clampWidth(state.startWidth + (event.clientX - state.startX)));
  };

  const stop = (): void => {
    drag.current = null;
  };

  return (
    <button
      type="button"
      // The accessible name says what the keys do, because that is the only
      // thing a keyboard user can do with it.
      aria-label={`Resize ${header} column — left and right arrows adjust`}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={stop}
      onPointerCancel={stop}
      onKeyDown={(event) => {
        const step = event.shiftKey ? 32 : 8;
        const current = width ?? event.currentTarget.closest('th')?.getBoundingClientRect().width;
        if (current === undefined) return;
        if (event.key === 'ArrowRight') {
          event.preventDefault();
          onResize(columnId, clampWidth(current + step));
        } else if (event.key === 'ArrowLeft') {
          event.preventDefault();
          onResize(columnId, clampWidth(current - step));
        }
      }}
      className={clsx(
        // `right-0`, NOT a negative offset. Hanging the handle 3px past the
        // cell made the LAST column's handle stick out past the table, which
        // put the wrapper 3px into overflow and drew a horizontal scrollbar
        // under every table in the app — on lists that fit perfectly. Matt saw
        // it the hour it deployed. Centring the grab strip on the divider is
        // worth 3px; a permanent scrollbar is not.
        'absolute inset-y-1 right-0 w-1.5 cursor-col-resize rounded touch-none',
        'hover:bg-control-line focus-visible:outline-2 focus-visible:outline-offset-1',
        'focus-visible:outline-brand-700',
      )}
    />
  );
}

function clampWidth(width: number): number {
  return Math.round(Math.min(MAX_COLUMN_WIDTH, Math.max(MIN_COLUMN_WIDTH, width)));
}

function SkeletonTableRows({ columns }: { readonly columns: number }): ReactNode {
  return (
    <>
      {Array.from({ length: 5 }, (_, rowIndex) => (
        <tr key={rowIndex} aria-hidden className="border-b border-hairline last:border-b-0">
          {Array.from({ length: columns }, (_, cellIndex) => (
            <td key={cellIndex} className="h-(--row-min-h) px-(--cell-px) py-(--cell-py)">
              <span className="block h-3.5 w-3/4 animate-pulse rounded bg-hairline" />
            </td>
          ))}
        </tr>
      ))}
    </>
  );
}

export function DataTable<T>({
  columns,
  rows,
  rowKey,
  mobileRow,
  sort,
  dir,
  onSort,
  label,
  empty,
  loading = false,
  onResize,
}: DataTableProps<T>): ReactNode {
  if (!loading && rows.length === 0 && empty !== undefined) return empty;

  return (
    <>
      {/* Record rows below 640px (docs/03 §6). */}
      <div className="rounded-card border border-card-line bg-card px-(--card-pad) sm:hidden">
        {loading ? (
          <div role="status" aria-label="Loading" className="py-2">
            {Array.from({ length: 5 }, (_, i) => (
              <div
                key={i}
                aria-hidden
                className="border-b border-hairline py-(--row-py) last:border-b-0"
              >
                <span className="block h-3.5 w-2/3 animate-pulse rounded bg-hairline" />
              </div>
            ))}
          </div>
        ) : (
          rows.map((row) => <div key={rowKey(row)}>{mobileRow(row)}</div>)
        )}
      </div>

      {/* The table, ≥640px. */}
      <div className="hidden overflow-x-auto rounded-card border border-card-line bg-card sm:block">
        <table className="w-full border-collapse text-body" aria-label={label}>
          <thead>
            <tr className="border-b border-card-line">
              {columns.map((column) => {
                const sorted = column.sortKey !== undefined && column.sortKey === sort;
                const alignClass = column.align === 'right' ? 'text-right' : 'text-left';
                return (
                  <th
                    key={column.header}
                    scope="col"
                    aria-sort={sorted ? (dir === 'desc' ? 'descending' : 'ascending') : undefined}
                    style={column.width === undefined ? undefined : { width: column.width }}
                    className={clsx(
                      'relative px-(--cell-px) py-(--thead-py) text-meta font-semibold text-ink2',
                      alignClass,
                    )}
                  >
                    {column.sortKey !== undefined && onSort !== undefined ? (
                      <button
                        type="button"
                        onClick={() => {
                          onSort(column.sortKey ?? '');
                        }}
                        className={clsx(
                          'inline-flex min-h-6 items-center gap-1 font-semibold hover:text-ink',
                          sorted ? 'text-ink' : 'text-ink2',
                        )}
                      >
                        {column.header}
                        {sorted ? (
                          dir === 'desc' ? (
                            <ArrowDown size={13} aria-hidden />
                          ) : (
                            <ArrowUp size={13} aria-hidden />
                          )
                        ) : (
                          // X-6.1: the affordance that says sorting exists at
                          // all. `--ink3` is the declared-decorative token, which
                          // is what this is — the header text carries the
                          // meaning and the aria-sort carries the state.
                          <ChevronsUpDown size={13} aria-hidden className="text-ink3" />
                        )}
                      </button>
                    ) : (
                      column.header
                    )}
                    {onResize !== undefined && column.id !== undefined ? (
                      <ResizeHandle
                        columnId={column.id}
                        header={column.header}
                        width={column.width}
                        onResize={onResize}
                      />
                    ) : null}
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <SkeletonTableRows columns={columns.length} />
            ) : (
              rows.map((row) => (
                <tr
                  key={rowKey(row)}
                  className="border-b border-hairline last:border-b-0 hover:bg-surface-hover"
                >
                  {columns.map((column) => (
                    <td
                      key={column.header}
                      className={clsx(
                        'h-(--row-min-h) px-(--cell-px) py-(--cell-py) text-ink',
                        column.align === 'right' && 'text-right',
                        column.className,
                      )}
                    >
                      {column.render(row)}
                    </td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </>
  );
}
